import { useCallback, useEffect, useRef, useState } from 'react';

export type AudioRecorderStatus = 'idle' | 'recording' | 'transcribing';

export type AudioRecorderErrorReason =
  | 'unsupported'
  | 'permission-denied'
  | 'no-microphone'
  | 'start-failed'
  | 'recording-failed'
  | 'no-speech'
  | 'transcription-failed';

type UseAudioRecorderParams = Readonly<{
  transcribe: (audio: Blob) => Promise<string>;
  onTranscript: (text: string) => void;
  onError: (reason: AudioRecorderErrorReason) => void;
  maxDurationMs: number;
}>;

type UseAudioRecorderResult = Readonly<{
  status: AudioRecorderStatus;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}>;

const startErrorReason = (error: unknown): AudioRecorderErrorReason => {
  const name = error instanceof DOMException ? error.name : '';

  if (name === 'NotAllowedError') return 'permission-denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'no-microphone';

  return 'start-failed';
};

const stopStream = (stream: MediaStream | undefined) =>
  stream?.getTracks().forEach((track) => track.stop());

/** Recording stops and transcribes on its own once `maxDurationMs` elapses. */
export const useAudioRecorder = ({
  transcribe,
  onTranscript,
  onError,
  maxDurationMs,
}: UseAudioRecorderParams): UseAudioRecorderResult => {
  const [status, setStatus] = useState<AudioRecorderStatus>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Claimed synchronously, because recorderRef is only set once getUserMedia resolves
  const startingRef = useRef(false);
  const isMountedRef = useRef(true);

  const callbacksRef = useRef({ transcribe, onTranscript, onError });

  callbacksRef.current = { transcribe, onTranscript, onError };

  const releaseRecorder = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    stopStream(recorderRef.current?.stream);
    recorderRef.current = null;
  }, []);

  // Releasing the tracks matters — otherwise the browser goes on showing its
  // recording indicator after the recorder is gone.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      const recorder = recorderRef.current;

      if (recorder) {
        recorder.ondataavailable = null;
        recorder.onstop = null;

        if (recorder.state === 'recording') {
          recorder.stop();
        }
      }

      releaseRecorder();
    };
  }, [releaseRecorder]);

  const startRecording = useCallback(async () => {
    if (recorderRef.current || startingRef.current) return;

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      callbacksRef.current.onError('unsupported');

      return;
    }

    startingRef.current = true;

    let stream: MediaStream;

    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      startingRef.current = false;
      callbacksRef.current.onError(startErrorReason(error));

      return;
    }

    if (!isMountedRef.current) {
      stopStream(stream);
      startingRef.current = false;

      return;
    }

    try {
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      // A fatal recorder error does not reliably queue a stop, so without this the
      // mic stays live behind a Stop button that no longer does anything.
      recorder.onerror = (event) => {
        console.error('Recording failed', event);

        recorder.ondataavailable = null;
        recorder.onstop = null;
        releaseRecorder();
        setStatus('idle');
        callbacksRef.current.onError('recording-failed');
      };

      recorder.onstop = async () => {
        releaseRecorder();

        // The recorder reports the container it actually produced, which differs
        // by browser (webm on Chrome, mp4 on iOS Safari).
        const audio = new Blob(chunks, { type: recorder.mimeType || chunks[0]?.type });

        if (audio.size === 0) {
          // Also how an absent mimeType or a codec failure presents, which no amount
          // of re-recording fixes.
          console.warn('Recording produced no audio', {
            mimeType: recorder.mimeType,
            chunks: chunks.length,
          });
          setStatus('idle');
          callbacksRef.current.onError('no-speech');

          return;
        }

        setStatus('transcribing');

        try {
          const text = (await callbacksRef.current.transcribe(audio)).trim();

          if (!isMountedRef.current) return;

          if (text) {
            callbacksRef.current.onTranscript(text);
          } else {
            callbacksRef.current.onError('no-speech');
          }
        } catch (error) {
          console.error('Audio transcription failed', error);

          if (isMountedRef.current) {
            callbacksRef.current.onError('transcription-failed');
          }
        } finally {
          setStatus('idle');
        }
      };

      recorderRef.current = recorder;
      recorder.start();
      setStatus('recording');

      timeoutRef.current = setTimeout(() => {
        if (recorderRef.current?.state === 'recording') {
          recorderRef.current.stop();
        }
      }, maxDurationMs);
    } catch (error) {
      console.error('Could not start recording', error);

      recorderRef.current = null;
      stopStream(stream);
      callbacksRef.current.onError('start-failed');
    } finally {
      startingRef.current = false;
    }
  }, [maxDurationMs, releaseRecorder]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  return { status, startRecording, stopRecording };
};
